import { observer } from 'mobx-react-lite'
import { ExpandableCardListEmptyState } from '@/components/ExpandableCardList'
import { Button } from '@/components/Button'
import { ButtonGroup } from '@/components/ButtonGroup'
import {
  type TaxPrepOrganizerViewModel,
  OrganizerStep,
} from '@/features/jobs/details/tax-type/tax-prep-organizer/TaxPrepOrganizerViewModel'
import { BusinessAccordion } from '@/features/jobs/details/tax-type/tax-prep-organizer/business/components/BusinessAccordion'
import { Id } from '@/infra/id'
import { RemoveBusinessDialog } from '@/features/jobs/details/tax-type/tax-prep-organizer/business/components/RemoveBusinessDialog'
import { StepCardListContainer } from '@/features/jobs/details/tax-type/tax-prep-organizer/components/StepCardListContainer'

interface BusinessStepCardListContainerProps {
  vm: TaxPrepOrganizerViewModel
  testId?: string
}

export const BusinessStepCardListContainer = observer(
  function BusinessStepCardListContainer({
    vm,
    testId,
  }: BusinessStepCardListContainerProps) {
    const title =
      vm.businessVms.length > 1
        ? `${vm.businessVms.length} Businesses`
        : vm.businessVms.length === 1
          ? '1 Business'
          : 'Business'

    return (
      <>
        <StepCardListContainer
          title={title}
          actions={
            <ButtonGroup>
              <Button
                label="Add business"
                variant="outline"
                onPress={vm.addBusinessDialogVm.showDialog}
                isSkeleton={vm.isLoading}
                testId={testId ? `${testId}-add-business-button` : undefined}
              />
              <Button
                iconLeft="tick"
                label="Confirm"
                isPending={vm.save.pending}
                onPress={() =>
                  vm.save({ type: 'step', step: OrganizerStep.Rental })
                }
                isSkeleton={vm.isLoading}
                testId={testId ? `${testId}-confirm-button` : undefined}
              />
            </ButtonGroup>
          }
          hasError={vm.showValidationError}
          renderEmptyState={() => (
            <ExpandableCardListEmptyState
              title={
                'No businesses added yet. Click "Add business" to get started.'
              }
              testId={testId ? `${testId}-empty-state` : undefined}
            />
          )}
          isSkeleton={vm.isLoading}
          testId={testId}
        >
          {vm.businessVms.map((businessVm, index) => (
            <BusinessAccordion
              key={businessVm.id || index}
              vm={businessVm}
              onRemove={() =>
                vm.removeBusinessDialogVm.showDialog({
                  id: Id.assert(businessVm.id),
                  displayName: businessVm.displayName,
                })
              }
              testId={
                testId ? `${testId}-business-accordion-${index}` : undefined
              }
            />
          ))}
        </StepCardListContainer>
        <RemoveBusinessDialog
          vm={vm.removeBusinessDialogVm}
          testId={testId ? `${testId}-remove-business-dialog` : undefined}
        />
      </>
    )
  },
)
